import React, { useState } from 'react';
import { Award, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CertificateGenerator = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState('');

  const checkEligibility = async () => {
    if (!email) return;
    
    setChecking(true);
    setError('');
    setEligibility(null);
    setCertificate(null);
    
    try {
      const response = await axios.get(`${API}/api/certificate/check/${encodeURIComponent(email)}`);
      setEligibility(response.data);
      if (response.data.name) {
        setName(response.data.name);
      }
    } catch (err) {
      setError('Unable to check eligibility. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const generateCertificate = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/certificate/generate`, {
        email,
        name: name || undefined
      });
      
      if (response.data.success) {
        setCertificate(response.data);
      } else {
        setError(response.data.message || 'Failed to generate certificate');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  const openCertificate = () => {
    if (certificate?.certificate_url) {
      window.open(`${API}${certificate.certificate_url}`, '_blank');
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-orange-500/30">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Award className="h-6 w-6 text-orange-500" />
          </div>
          <div>
            <CardTitle className="text-white">Certificate of Completion</CardTitle>
            <CardDescription>Get your certificate after completing the 7-day course</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Success State */}
        {certificate ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Certificate Ready!</h3>
            <p className="text-gray-400 mb-4">
              Congratulations, {certificate.name}! Your certificate has been generated.
            </p>
            <Button 
              onClick={openCertificate}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View & Print Certificate
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              Certificate ID: {certificate.certificate_id}
            </p>
          </div>
        ) : (
          <>
            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Email used for course enrollment</label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                />
                <Button
                  onClick={checkEligibility}
                  disabled={!email || checking}
                  variant="outline"
                  className="border-gray-700 text-gray-300"
                >
                  {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                </Button>
              </div>
            </div>

            {/* Eligibility Result */}
            {eligibility && (
              <div className={`p-4 rounded-lg ${
                eligibility.eligible && eligibility.completed 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                {eligibility.eligible && eligibility.completed ? (
                  <>
                    <p className="text-green-400 font-medium mb-3">
                      ✓ You've completed the course! Generate your certificate below.
                    </p>
                    {eligibility.existing_certificate ? (
                      <div>
                        <p className="text-gray-400 text-sm mb-2">
                          You already have a certificate:
                        </p>
                        <Button
                          onClick={() => window.open(`${API}/api/certificate/view/${eligibility.existing_certificate}`, '_blank')}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          View Existing Certificate
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-400 mb-1 block">Your Name (for certificate)</label>
                          <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your full name"
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <Button
                          onClick={generateCertificate}
                          disabled={loading}
                          className="w-full bg-orange-500 hover:bg-orange-600"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Award className="h-4 w-4 mr-2" />
                              Generate Certificate
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                ) : eligibility.reason === 'not_enrolled' ? (
                  <p className="text-yellow-400">
                    No enrollment found. <a href="/resources" className="underline">Enroll in the course</a> first!
                  </p>
                ) : (
                  <p className="text-yellow-400">
                    You're on Day {eligibility.current_day} of 7. Complete {eligibility.days_remaining} more days to earn your certificate!
                  </p>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CertificateGenerator;
